import { useMutation } from 'react-query'
import { UseMutationOptions } from 'react-query/types/react/types'
import { sendToApi } from 'api/client'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { GeneratedTheme } from 'screens/ContentPlan/CreateCampaignModalV2/Types'

export interface ThemePreviewVariables {
  content_strategy_type: string
  start_date: string
  length: number
  goal?: string
  existing_themes?: Array<Partial<GeneratedTheme> & { id: string; description: string }>
  theme_details?: string
  source_ids?: Array<{ type: string; id: number }>
}

interface Props {
  brandPlanId: number
}

interface Data {
  theme: GeneratedTheme
}

export type Options = Omit<UseMutationOptions<Data, Error, ThemePreviewVariables>, 'mutationFn'>

// Longer than Heroku's 30s web-request timeout (the whole point of going
// async — see BLZ-15958) but bounded so a wedged generation still resolves.
const POLL_INTERVAL_MS = 2000
const TIMEOUT_MS = 90_000

const FAILURE_MESSAGE = 'Failed to generate theme preview. Please try again.'

type ThemeGenerationStatus = 'pending' | 'started' | 'successful' | 'failed'

interface ThemeGeneration {
  id: number
  themes: GeneratedTheme[]
  status: ThemeGenerationStatus
}

const startThemeGeneration = async (
  workspaceId: number,
  brandPlanId: number,
  variables: ThemePreviewVariables,
): Promise<ThemeGeneration> => {
  const res = await sendToApi<{ themes: ThemeGeneration }>(`/w/${workspaceId}/brand_plans/${brandPlanId}/themes`, {
    method: 'POST',
    body: variables,
  })

  if (res.type !== SUCCESS_RESPONSE_TYPE) {
    throw new Error(FAILURE_MESSAGE)
  }

  return res.data.themes
}

const fetchThemeGeneration = async (id: number): Promise<ThemeGeneration> => {
  const res = await sendToApi<{ themes: ThemeGeneration }>(`/brand_plan_themes/${id}`)

  if (res.type !== SUCCESS_RESPONSE_TYPE) {
    throw new Error(FAILURE_MESSAGE)
  }

  return res.data.themes
}

const isPending = (generation: ThemeGeneration) => generation.status === 'pending' || generation.status === 'started'

export const useGenerateThemePreview = ({ brandPlanId }: Props, options?: Options) => {
  const workspaceId = useCurrentWorkspaceId()

  return useMutation<Data, Error, ThemePreviewVariables>(async (variables) => {
    let generation = await startThemeGeneration(workspaceId, brandPlanId, variables)
    const deadline = Date.now() + TIMEOUT_MS

    while (isPending(generation)) {
      if (Date.now() > deadline) {
        throw new Error(FAILURE_MESSAGE)
      }

      await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS))
      generation = await fetchThemeGeneration(generation.id)
    }

    if (generation.status !== 'successful' || !generation.themes[0]) {
      throw new Error(FAILURE_MESSAGE)
    }

    return { theme: generation.themes[0] }
  }, options)
}
